import { Link } from 'react-router-dom';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { contentList, type ContentCategory } from '@/mocks/content';
import {
  getByCategory,
  sortByHot,
  sortByLatest,
} from '@/utils/content';
import HeroCarousel from '@/pages/home/components/HeroCarousel';
import CategoryEntries from '@/pages/home/components/CategoryEntries';
import LatestNews from '@/pages/home/components/LatestNews';
import HotEntertainment from '@/pages/home/components/HotEntertainment';
import LifestyleTopics from '@/pages/home/components/LifestyleTopics';

export default function Home() {
  useDocumentTitle(
    '小-多元内容聚合平台，资讯、娱乐全覆盖',
    '小为您提供丰富的新闻资讯、影视娱乐、生活服务等多元内容，满足用户日常信息需求，打造一站式内容获取平台。',
    '小,资讯平台,娱乐新闻,生活服务,内容聚合,信息门户'
  );

  const featured = sortByLatest(contentList.filter((i) => i.featured && i.category !== 'entertainment'));
  const latestNews = sortByLatest(getByCategory('news')).slice(0, 5);
  const hotEntertainment = sortByHot(getByCategory('entertainment')).slice(0, 4);
  const lifestyleTopics = getByCategory('lifestyle').slice(0, 6);

  const counts: Record<ContentCategory, number> = {
    news: getByCategory('news').length,
    entertainment: getByCategory('entertainment').length,
    lifestyle: getByCategory('lifestyle').length,
  };

  const ticker = sortByLatest(contentList).slice(0, 6);

  return (
    <>
      <HeroCarousel items={featured} />

      {/* 热点速览 */}
      <div className="border-b border-background-200/70 bg-background-50">
        <div className="mx-auto flex w-full max-w-[1280px] items-center gap-4 px-4 py-3 md:px-6">
          <span className="flex flex-shrink-0 items-center gap-1.5 text-sm font-semibold text-accent-600">
            <i className="ri-flashlight-fill"></i>
            热点速览
          </span>
          <div className="portal-scroll flex items-center gap-5 overflow-x-auto whitespace-nowrap text-sm">
            {ticker.map((item) => (
              <Link
                key={item.id}
                to={`/detail/${item.id}`}
                className="text-foreground-700 transition-colors hover:text-primary-600"
              >
                {item.title}
              </Link>
            ))}
          </div>
        </div>
      </div>

      <CategoryEntries counts={counts} />
      <LatestNews items={latestNews} />
      <HotEntertainment items={hotEntertainment} />
      <LifestyleTopics items={lifestyleTopics} />

      {/* 站点简介 */}
      <section className="bg-primary-900 py-12 text-background-100 md:py-16">
        <div className="mx-auto flex w-full max-w-[1280px] flex-col gap-8 px-4 md:flex-row md:items-center md:justify-between md:px-6">
          <div className="max-w-2xl">
            <h2 className="mb-4 font-heading text-xl font-bold text-background-50 md:text-2xl">
              关于「小」· 一站式内容获取平台
            </h2>
            <p className="text-sm leading-relaxed text-background-200 md:text-base">
              「小」聚合新闻资讯、影视娱乐与生活服务三大板块，用清晰的分类和便捷的搜索，帮助你在信息海洋中快速找到想看的内容。无需注册，打开即用。
            </p>
            <div className="mt-7 flex flex-wrap gap-8">
              <div>
                <div className="font-heading text-2xl font-bold text-accent-400">{contentList.length}+</div>
                <div className="mt-1 text-xs text-background-300">精选内容</div>
              </div>
              <div>
                <div className="font-heading text-2xl font-bold text-accent-400">3</div>
                <div className="mt-1 text-xs text-background-300">内容频道</div>
              </div>
              <div>
                <div className="font-heading text-2xl font-bold text-accent-400">4</div>
                <div className="mt-1 text-xs text-background-300">生活专题</div>
              </div>
            </div>
          </div>
          <div className="flex flex-shrink-0 flex-col gap-3">
            <Link
              to="/about"
              className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-7 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600 dark:text-foreground-950"
            >
              了解平台
              <i className="ri-arrow-right-line"></i>
            </Link>
            <Link
              to="/search"
              className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-100/40 px-7 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/10"
            >
              <i className="ri-search-line"></i>
              全站搜索
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}